import { useLayoutEffect } from 'react';
import { useMutation } from 'react-query';
import { cancelPaymentPlan } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { usePollWorkflowsHook } from '@frontend/payments-hooks';
import { theme } from '@frontend/theme';
import { Modal, Text, useAlert, useModalLoadingState } from '@frontend/design-system';
import { usePaymentPlanDetails } from '../../hooks/payment-plans/use-payment-plan-details';

export const CancelPaymentPlanModal = ({
  planId,
  planName,
  onClose,
}: {
  planId: string;
  planName: string;
  locationId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('payments');
  const alert = useAlert();
  const { setLoading } = useModalLoadingState();
  const { refetch } = usePaymentPlanDetails({ paymentPlanId: planId });

  const { startPolling, loading: isPolling } = usePollWorkflowsHook({
    onPollingEnd: async () => {
      await refetch();
      onClose();
    },
    onComplete: () => {
      alert.success(t('Plan canceled successfully.'));
    },
    onFailure: () => {
      alert.error(t(`Error cancelling payment plan. Please try again.`));
    },
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: () => cancelPaymentPlan(planId),
    onSuccess: async (data) => {
      startPolling([data.data.workflowId]);
    },
    onError: () => {
      alert.error(t(`Error cancelling payment plan. Please try again.`));
    },
  });

  useLayoutEffect(() => {
    setLoading(isLoading || isPolling);
  }, [isLoading, isPolling]);

  return (
    <>
      <Modal.Header textAlign='left' onClose={onClose}>
        {t(`Cancel Payment Plan`)}
      </Modal.Header>
      <Modal.Body css={{ marginTop: theme.spacing(2) }}>
        <Text>
          You are about to cancel this payment plan. This will cancel any scheduled payments towards this plan.
        </Text>
        <br />
        <Text>
          Are you sure you want to cancel <strong>{planName}</strong>?
        </Text>
      </Modal.Body>
      <Modal.Footer
        primary={{
          onClick: () => mutate(),
          label: t('Confirm'),
          destructive: true,
        }}
        secondary={{
          onClick: onClose,
          label: t('Never Mind'),
        }}
      />
    </>
  );
};
